import { useState } from "react";

import { CoilType } from "@eisox/api/core-api";
import { Button, CanAccess, DropdownMenu } from "@eisox/design-system";
import { AirHandlerUnitIcon, BracesIcon, ChevronDownIcon, NetworkIcon, NutIcon, RoomIcon } from "@eisox/icons";
import { Link } from "@tanstack/react-router";

import { VersionGate } from "~/components/version-gate";
// Deep imports: the module feature barrel reaches back into this feature.
import { InitDataDialog } from "~/features/module/components/init-data-dialog";
import { VariableDetailsDialog } from "~/features/module/components/variable-details-dialog";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

import { useAirHandlerUnitContext } from "../providers/air-handler-unit.provider";
import { HydraulicCoilAssociationDialog } from "./hydraulic-coil-association-dialog";
import { RoomAssociationDialog } from "./room-association-dialog";

const NAME = "ActionDropdownMenu";

enum ActionDialog {
  VARIABLE_DETAILS,
  INIT_DATA,
  HYDRAULIC_COIL_ASSOCIATION,
  ROOM_ASSOCIATION,
}

interface ActionDropdownMenuProps {
  buildingId: string;
  airHandlerUnitId: string;
}

export const ActionDropdownMenu: React.FC<ActionDropdownMenuProps> = ({ buildingId, airHandlerUnitId }) => {
  const { permissions, gateways, modules, airHandlerUnits: buildingAirHandlerUnits } = useBuildingContext(NAME);
  const { airHandlerUnits, airHandlerUnit } = useAirHandlerUnitContext(NAME);

  const displayHydraulicCoilAssociation = airHandlerUnits.some(
    unit => (unit.coils?.filter(coil => coil.coilType !== CoilType.ELECTRIC).length ?? 0) > 0,
  );

  const primaryBuildingAirHandlerUnit = buildingAirHandlerUnits.find(ahu => ahu.id === airHandlerUnit[0]?.id);
  // The page's own unit, not the group's first one: grouped units can sit on different modules.
  const currentBuildingAirHandlerUnit = buildingAirHandlerUnits.find(ahu => ahu.id === airHandlerUnitId);
  const gatewaySoftwareVersion = gateways.find(
    gateway => gateway.id === modules.find(module => module.id === primaryBuildingAirHandlerUnit?.moduleId)?.gatewayId,
  )?.softwareVersion;

  const [openDialog, setOpenDialog] = useState<ActionDialog>();

  const handleCloseDialog = () => setOpenDialog(undefined);

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <Button>
            {m.actions()} <ChevronDownIcon />
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="end" sideOffset={10}>
          <CanAccess isAuthorized={permissions.module?.airHandlerUnit?.variableData?.read}>
            <DropdownMenu.Item onSelect={() => setOpenDialog(ActionDialog.VARIABLE_DETAILS)}>
              <BracesIcon /> {m.variables_details()}
            </DropdownMenu.Item>
          </CanAccess>
          <CanAccess isAuthorized={permissions.module?.airHandlerUnit?.variableData?.update}>
            <DropdownMenu.Item onSelect={() => setOpenDialog(ActionDialog.INIT_DATA)}>
              <BracesIcon /> {m.init_data_by_bms_module()}
            </DropdownMenu.Item>
          </CanAccess>
          {displayHydraulicCoilAssociation && (
            <DropdownMenu.Item onSelect={() => setOpenDialog(ActionDialog.HYDRAULIC_COIL_ASSOCIATION)}>
              <NetworkIcon /> {m.hydraulic_coil_association()}
            </DropdownMenu.Item>
          )}
          <VersionGate
            devices={[
              {
                type: "gateway",
                current: gatewaySoftwareVersion,
                feature: "AIR_HANDLER_UNIT_ROOM_ASSOCIATION",
              },
            ]}
          >
            {({ disabled: roomAssociationDisabled }) =>
              roomAssociationDisabled ? (
                <DropdownMenu.Item className="data-disabled:pointer-events-auto data-disabled:cursor-default" disabled>
                  <RoomIcon /> {m.air_handler_unit_room_management()}
                </DropdownMenu.Item>
              ) : (
                <DropdownMenu.Item onSelect={() => setOpenDialog(ActionDialog.ROOM_ASSOCIATION)}>
                  <RoomIcon /> {m.air_handler_unit_room_management()}
                </DropdownMenu.Item>
              )
            }
          </VersionGate>
          <CanAccess isAuthorized={permissions.module?.read}>
            <DropdownMenu.Item asChild>
              <Link to={"/buildings/$buildingId/settings/bms/modules"} params={{ buildingId }}>
                <NutIcon /> {m.bms_module_management()}
              </Link>
            </DropdownMenu.Item>
          </CanAccess>
          <DropdownMenu.Item asChild>
            <Link to={"/buildings/$buildingId/settings/bms/ahu-groups"} params={{ buildingId }}>
              <AirHandlerUnitIcon /> {m.group_management()}
            </Link>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Root>

      {openDialog === ActionDialog.VARIABLE_DETAILS && (
        <VariableDetailsDialog
          open
          onOpenChange={handleCloseDialog}
          buildingId={buildingId}
          equipments={buildingAirHandlerUnits}
          defaultModuleId={currentBuildingAirHandlerUnit?.moduleId}
        />
      )}
      {openDialog === ActionDialog.INIT_DATA && (
        <InitDataDialog open onOpenChange={handleCloseDialog} buildingId={buildingId} />
      )}
      {openDialog === ActionDialog.HYDRAULIC_COIL_ASSOCIATION && (
        <HydraulicCoilAssociationDialog open onOpenChange={handleCloseDialog} buildingId={buildingId} />
      )}
      {openDialog === ActionDialog.ROOM_ASSOCIATION && (
        <RoomAssociationDialog open onOpenChange={handleCloseDialog} buildingId={buildingId} />
      )}
    </>
  );
};

ActionDropdownMenu.displayName = NAME;
